import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { range } from "d3";
import * as BookingAnalyticsService from "_services/bookingAnalytics-service";
import * as BookingService from "_services/booking-service";

const initialState = {
  countInbound: null,
  countOutbound: null,
  countInboundForecast: null,
  countOutboundForecast: null,

  status: "idle",
  error: null,
  analyticsCountOutbound: null,
  analyticsCountOutbound: null,
  analyticsCountInboundStatus: "loading",
  analyticsCountOutboundStatus: "loading",
  analyticsCountInboundByHour: null,
  analyticsCountOutboundByHour: null,
  analyticsCountOutboundByHourNoReduce: null,
  analyticsCountInboundByHourForForecast: null,
  analyticsCountOutboundByHourForForecast: null,
  analyticsCountOutboundByHourStatus: "loading",
  analyticsCountOutboundByHourStatus: "loading",
  analyticsStatsDayComparedToLastWeek: null,

  prenotifiedCount: 0,
  nonprenotifiedCount: 0,

  bookingBreakdownInbound: null,
  bookingBreakdownOutbound: null,
  bookingBreakdownStatus: "idle",
  bookingBreakdownError: null,

  arrivalsForecast: null,
  arrivalsForecastStatus: "idle",
  countForecast: 0,
  departuresForecast: null,
  departuresForecastStatus: "idle",

  countForecast: 0,

  arrivalsForecastRange: null,
  arrivalsForecastRangeStatus: "idle",
  arrivalsForecastRangeError: null,

  departuresForecastRange: null,
  departuresForecastRangeStatus: "idle",
  departuresForecastRangeError: null,

  arrivalsFlightsForecastRange: null,
  arrivalsFlightsForecastRangeStatus: "idle",
  arrivalsFlightsForecastRangeError: null,

  departuresFlightsForecastRange: null,
  departuresFlightsForecastRangeStatus: "idle",
  departuresFlightsForecastRangeError: null,


  analyticsStats: null,
  range: "week",
  typeSelector: "none",
  analyticsStatsStatus: "idle",

  // bookings due time
  bookingsDueTimes: null,
  bookingsDueTimesStatus: "idle",
  bookingsDueTimesError: null,

  // bookings due time between airports
  bookingsDueTimesBetweenAirports: null,
  bookingsDueTimesBetweenAirportsStatus: "idle",
  bookingsDueTimesBetweenAirportsError: null,

  // weekly forecast analytics
  weeklyAnalyticsCountInboundByHourForForecast: null,
  weeklyAnalyticsCountInboundByHourForForecastStatus: "idle",
  weeklyAnalyticsCountInboundByHourForForecastError: null,

  weeklyAnalyticsCountOutboundByHourForForecast: null,
  weeklyAnalyticsCountOutboundByHourForForecastStatus: "idle",
  weeklyAnalyticsCountOutboundByHourForForecastError: null,

  // weekly arrivals forecast
  weeklyArrivalsForecast: null,
  weeklyArrivalsForecastStatus: "idle",
  weeklyArrivalsForecastError: null,

  // weekly departures forecast
  weeklyDeparturesForecast: null,
  weeklyDeparturesForecastStatus: "idle",
  weeklyDeparturesForecastError: null,

  // no show counts
  noShowCountOutbound: null,
  noShowCountStatus: "idle",
  noShowCountError: null,

  // show calendar counts flags
  showDepartures: true,
  showArrivals: true,
  showTotals: true,
  showForecast: true,
  showWeather: true,
  showHighlights: true,

  // status flags
  getBookingsForecastCountOutboundStatus: 'idle',
  getBookingsForecastCountInboundStatus: 'idle',
  getBookingsCountOutboundStatus: 'idle',
  getBookingsCountInboundStatus: 'idle',
  getPrenotifiedBookingsStatus: 'idle',


  // quarterly count
  inboundDocuments: null,
  getInboundDocumentsStatus: 'idle',
  getInboundDocumentsError: null,

  outboundDocuments: null,
  getOutboundDocumentsStatus: 'idle',
  getOutboundDocumentsError: null,
};

//#region functions

export const getInboundDocuments = createAsyncThunk(
  "bookingsCount/getInboundDocuments",
  async (params) => {
    return BookingService.getBookingDocuments(params);
  }
);

export const getOutboundDocuments = createAsyncThunk(
  "bookingsCount/getOutboundDocuments",
  async (params) => {
    return BookingService.getBookingDocuments(params);
  }
);

export const getNoShowCount = createAsyncThunk(
  "bookingsCount/getNoShowCount",
  async (params) => {
    return BookingService.getNoShowCount(params);
  }
);

// export forecast table list
export const exportForecast = createAsyncThunk(
  "bookingsCount/exportForecast",
  async (params) => {
    return BookingService.exportForecast(params);
  }
);

// booking breakdown
export const getBookingBreakdownInbound = createAsyncThunk(
  "bookingsCount/getBookingBreakdownInbound",
  async (params) => {
    return BookingService.getBookingBreakdownInbound(params);
  }
);

export const getDeparturesForecast = createAsyncThunk('bookingsCount/getDeparturesForecast', async (params, signal) => {
  return BookingService.getDepartures(params, signal);
});

export const getDeparturesForecastRange = createAsyncThunk('bookingsCount/getDeparturesForecastRange', async (params, signal) => {
  return BookingService.getDeparturesForecastRange(params, signal);
});
// get count for list
export const getDeparturesCountForecast = createAsyncThunk('bookingsCount/getDeparturesCountForecast', async (params, signal) => {
  return BookingService.getArrivalsOrDeparturesCount(params, signal);
});

// booking breakdown
export const getBookingBreakdownOutbound = createAsyncThunk(
  "bookingsCount/getBookingBreakdownOutbound",
  async (params) => {
    return BookingService.getBookingBreakdownInbound(params);
  }
);

// get weekly departure forecast
export const getWeeklyDeparturesForecast = createAsyncThunk(
  "bookingsCount/getWeeklyDeparturesForecast",
  async (params, signal) => {
    return BookingService.getWeeklyForecast(
      { ...params, type: "outbound" },
      signal
    );
  }
);

// get arrival forecast
export const getArrivalsForecast = createAsyncThunk(
  "bookingCounts/getArrivalsForecast",
  async (params, signal) => {
    return BookingService.getArrivals(params, signal);
  }
);

// get arrival forecast
export const getArrivalsForecastRange = createAsyncThunk(
  "bookingCounts/getArrivalsForecastRange",
  async (params, signal) => {
    return BookingService.getArrivalsForecastRange(params, signal);
  }
);

// get weekly arrival forecast
export const getWeeklyArrivalsForecast = createAsyncThunk(
  "bookingCounts/getWeeklyArrivalsForecast",
  async (params, signal) => {
    return BookingService.getWeeklyForecast(
      { ...params, type: "inbound" },
      signal
    );
  }
);

// get count for list
export const getArrivalsCountForecast = createAsyncThunk(
  "bookingCounts/getArrivalsCountForecast",
  async (params, signal) => {
    return BookingService.getArrivalsOrDeparturesCount(params, signal);
  }
);

export const getAnalyticsStats = createAsyncThunk(
  "bookingsCount/getAnalyticsStats",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsStats(params, signal);
  }
);

export const getAnalyticsStatsDayComparedToLastWeek = createAsyncThunk(
  "bookingsCount/getAnalyticsStatsDayComparedToLastWeek",
  async (params, signal) => {
    let today = await BookingAnalyticsService.getBookingsStats(
      {
        ...params,
        date: new Date().toISOString().split("T")[0],
        date: new Date().toISOString().split("T")[0],
        endDate: new Date().toISOString().split("T")[0],
      },
      signal
    );
    let lastWeek = await BookingAnalyticsService.getBookingsStats(
      {
        ...params,
        date: new Date(new Date().setDate(new Date().getDate() - 7))
          .toISOString()
          .split("T")[0],
        endDate: new Date(new Date().setDate(new Date().getDate() - 7))
          .toISOString()
          .split("T")[0],
      },
      signal
    );
    return {
      inboundDiff:
        ((today.inbound - lastWeek.inbound) / lastWeek.inbound) * 100,
      outboundDiff:
        ((today.outbound - lastWeek.inbound) / lastWeek.outbound) * 100,
      totalDiff:
        ((today.inbound +
          today.outbound -
          (lastWeek.inbound + lastWeek.outbound)) /
          (lastWeek.inbound + lastWeek.outbound)) *
        100,
      lastWeekInbound: lastWeek.inbound,
      lastWeekOutbound: lastWeek.outbound,
      thisWeekInbound: today.inbound,
      thisWeekOutbound: today.outbound,
    };
  }
);

export const getAnalyticsCountInbound = createAsyncThunk(
  "bookingsCount/getAnalyticsCountInbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "inbound" },
      signal
    );
  }
);

export const getAnalyticsCountOutbound = createAsyncThunk(
  "bookingsCount/getAnalyticsCountOutbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound" },
      signal
    );
  }
);

export const getBookingsForecastCountInbound = createAsyncThunk(
  "bookingsCount/getBookingsForecastCountInbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "inbound", forecast: "true" },
      signal
    );
  }
);

export const getBookingsForecastCountOutbound = createAsyncThunk(
  "bookingsCount/getBookingsForecastCountOutbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound", forecast: "true" },
      signal
    );
  }
);

export const getBookingsCountInbound = createAsyncThunk(
  "bookingsCount/getBookingsCountInbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "inbound" },
      signal
    );
  }
);

export const getBookingsCountOutbound = createAsyncThunk(
  "bookingsCount/getBookingsCountOutbound",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound" },
      signal
    );
  }
);

export const getAnalyticsCountInboundByHour = createAsyncThunk(
  "bookingsCount/getAnalyticsCountInboundByHour",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "inbound", byHour: true },
      signal
    );
  }
);

export const getAnalyticsCountOutboundByHour = createAsyncThunk(
  "bookingsCount/getAnalyticsCountOutboundByHour",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound", byHour: true },
      signal
    );
  }
);

export const getAnalyticsCountOutboundByHourNoReduce = createAsyncThunk(
  "bookingsCount/getAnalyticsCountOutboundByHourNoReduce",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound", byHour: true, reduce: false },
      signal
    );
  }
);

export const getAnalyticsCountInboundByHourForForecast = createAsyncThunk(
  "bookingsCount/getAnalyticsCountInboundByHourForForecast",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "inbound", byHour: true, source: 'none' },
      signal
    );
  }
);

export const getAnalyticsCountOutboundByHourForForecast = createAsyncThunk(
  "bookingsCount/getAnalyticsCountOutboundByHourForForecast",
  async (params, signal) => {
    return BookingAnalyticsService.getBookingsCount(
      { ...params, type: "outbound", byHour: true, source: 'none' },
      signal
    );
  }
);

export const getWeeklyAnalyticsCountInboundByHourForForecast = createAsyncThunk(
  "bookingsCount/getWeeklyAnalyticsCountInboundByHourForForecast",
  async (params, signal) => {
    return BookingAnalyticsService.getWeeklyAnalyticsCountByHourForForecast(
      { ...params, type: "inbound", byHour: true, source: "none" },
      signal
    );
  }
);

export const getWeeklyAnalyticsCountOutboundByHourForForecast =
  createAsyncThunk(
    "bookingsCount/getWeeklyAnalyticsCountOutboundByHourForForecast",
    async (params, signal) => {
      return BookingAnalyticsService.getWeeklyAnalyticsCountByHourForForecast(
        { ...params, type: "outbound", byHour: true, source: "none" },
        signal
      );
    }
  );

// get prenotified bookings
export const getPrenotifiedBookings = createAsyncThunk("bookingCounts/getPrenotifiedBookings", async (params) => {
  return BookingService.getPrenotifiedBookings(params);
}
);

// get all non prenotified bookings
export const getNonPrenotifiedBookings = createAsyncThunk(
  "bookingCounts/getNonPrenotifiedBookings",
  async (params) => {
    return BookingService.getPrenotifiedBookings(params);
  }
);

export const getBookingsDueTimes = createAsyncThunk(
  "bookingsCount/getBookingsDueTimes",
  async (props) => {
    return BookingService.getBookingsDueTimes(props);
  }
);

export const getBookingsDueTimesBetweenAirports = createAsyncThunk(
  "bookingsCount/getBookingsDueTimesBetweenAirports",
  async (props) => {
    return BookingService.getBookingsDueTimesBetweenAirports(props);
  }
);
//flight forecast by date range
export const getDeparturesFlightForecastByDateRange = createAsyncThunk('bookingsCount/getDeparturesFlightForecastByDateRange', async (params, signal) => {
  return BookingService.getDeparturesForecastRange(params, signal);
});
export const getArrivalsFlightForecastByDateRange = createAsyncThunk('bookingsCount/getArrivalssFlightForecastByDateRange', async (params, signal) => {
  return BookingService.getArrivalsForecastRange(params, signal);
});

const bookingsCountSlice = createSlice({
  name: "count",
  initialState,
  reducers: {
    reset: (state) => initialState,
    setRange: (state, action) => {
      return {
        ...state,
        range: action.payload,
      };
    },
    setTypeSelector: (state, action) => {
      return {
        ...state,
        typeSelector: action.payload,
      };
    },
    setShowDepartures: (state, action) => {
      return {
        ...state,
        showDepartures: action.payload,
      };
    },
    setShowArrivals: (state, action) => {
      return {
        ...state,
        showArrivals: action.payload,
      };
    },
    setShowForcast: (state, action) => {
      return {
        ...state,
        showForecast: action.payload,
      };
    },
    setShowTotals: (state, action) => {
      return {
        ...state,
        showTotals: action.payload,
      };
    },
    setShowWeather: (state, action) => {
      return {
        ...state,
        showWeather: action.payload,
      };
    },
    setShowHighlights: (state, action) => {
      return {
        ...state,
        showHighlights: action.payload,
      };
    },
    resetForecastBookingsRange: (state) => {
      state.arrivalsForecastRange = null;
      state.arrivalsForecastRangeStatus = "idle";
      state.arrivalsForecastRangeError = null;
      state.departuresForecastRange = null;
      state.departuresForecastRangeStatus = "idle";
      state.departuresForecastRangeError = null;
    },
    resetForecastStatus: (state) => {
      state.arrivalsForecastStatus = "idle";
      state.departuresForecastStatus = "idle";
    },
    resetAnalyticsStatsStatus: (state) => {
      state.analyticsStatsStatus = "idle";
    },
    resetForecastFlights: (state) => {
      state.departuresFlightsForecastRangeStatus = "idle";
      state.arrivalsFlightsForecastRangeStatus = "idle";

      state.arrivalsFlightsForecastRangeError = null;
      state.departuresFlightsForecastRangeError = null;

      state.departuresFlightsForecastRange = null;
      state.arrivalsFlightsForecastRange = null;
    }

  },
  extraReducers: {

    [getInboundDocuments.pending]: (state, action) => {
      state.getInboundDocumentsStatus = 'idle';
      state.getInboundDocumentsError = null;
    },
    [getInboundDocuments.fulfilled]: (state, action) => {
      state.inboundDocuments = action.payload;
      state.getInboundDocumentsStatus = "succeeded";
    },
    [getInboundDocuments.rejected]: (state, action) => {
      state.inboundDocuments = null;
      state.getInboundDocumentsStatus = "failed";
    },
    [getOutboundDocuments.pending]: (state, action) => {
      state.getOutboundDocumentsStatus = 'idle';
      state.getOutboundDocumentsError = null;
    },
    [getOutboundDocuments.fulfilled]: (state, action) => {
      state.outboundDocuments = action.payload;
      state.getOutboundDocumentsStatus = "succeeded";
      state.getOutboundDocumentsError = null;
    },
    [getOutboundDocuments.rejected]: (state, action) => {
      state.outboundDocuments = null;
      state.getOutboundDocumentsStatus = "failed";
    },


    [getNoShowCount.pending]: (state, action) => {
      state.noShowCountOutbound = null;
      state.noShowCountStatus = "loading";
      state.noShowCountError = null;
    },
    [getNoShowCount.fulfilled]: (state, action) => {
      state.noShowCountOutbound = action.payload;
      state.noShowCountStatus = "succeeded";
    },
    [getNoShowCount.rejected]: (state, action) => {
      state.noShowCountOutbound = null;
      state.noShowCountStatus = "failed";
    },
    [getPrenotifiedBookings.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
        statusObj: { ...state.statusObj, getPrenotifiedBookings: 'loading' },
      }
    },
    [getPrenotifiedBookings.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        statusObj: { ...state.statusObj, getPrenotifiedBookings: 'succeeded' },
        prenotifiedCount: action.payload,
      };
    },
    [getPrenotifiedBookings.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        getPrenotifiedBookingsStatus: "failed",

        statusObj: { ...state.statusObj, getPrenotifiedBookings: "failed" },
        error: action.error.message,
      };
    },
    [getNonPrenotifiedBookings.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getNonPrenotifiedBookings: "loading" },
      };
    },
    [getNonPrenotifiedBookings.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getNonPrenotifiedBookings: "succeeded",
        },
        nonprenotifiedCount: action.payload,
      };
    },
    [getNonPrenotifiedBookings.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getNonPrenotifiedBookings: "failed" },
        error: action.error.message,
      };
    },
    [getBookingsCountInbound.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        getBookingsCountInboundStatus: "loading",
        statusObj: { ...state.statusObj, getBookingsCountInbound: "loading" },
      };
    },
    [getBookingsCountInbound.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        getBookingsCountInboundStatus: "succeeded",
        statusObj: { ...state.statusObj, getBookingsCountInbound: "succeeded" },
        countInbound: action.payload,
      };
    },
    [getBookingsCountInbound.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        getBookingsCountInboundStatus: "failed",
        statusObj: { ...state.statusObj, getBookingsCountInbound: "failed" },
        countInbound: 0,
        error: action.error.message,
      };
    },
    [getBookingsCountOutbound.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        getBookingsCountOutboundStatus: "loading",
        statusObj: { ...state.statusObj, getBookingsCountOutbound: "loading" },
      };
    },
    [getBookingsCountOutbound.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        getBookingsCountOutboundStatus: "succeeded",
        statusObj: {
          ...state.statusObj,
          getBookingsCountOutbound: "succeeded",
        },
        countOutbound: action.payload,
      };
    },
    [getBookingsCountOutbound.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        getBookingsCountOutboundStatus: "failed",
        statusObj: { ...state.statusObj, getBookingsCountOutbound: "failed" },
        error: action.error.message,
        countOutbound: 0,
      };
    },
    [getBookingsForecastCountInbound.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        getBookingsForecastCountInboundStatus: 'loading',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountInbound: "loading",
        },
      };
    },
    [getBookingsForecastCountInbound.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        getBookingsForecastCountInboundStatus: 'succeeded',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountInbound: "succeeded",
        },
        countInboundForecast: action.payload,
      };
    },
    [getBookingsForecastCountInbound.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        getBookingsForecastCountInboundStatus: 'failed',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountInbound: "failed",
        },
        error: action.error.message,
      };
    },
    [getBookingsForecastCountOutbound.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        getBookingsForecastCountOutboundStatus: 'loading',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountOutbound: "loading",
        },
      };
    },
    [getBookingsForecastCountOutbound.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        getBookingsForecastCountOutboundStatus: 'succeeded',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountOutbound: "succeeded",
        },
        countOutboundForecast: action.payload,
      };
    },
    [getBookingsForecastCountOutbound.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        getBookingsForecastCountOutboundStatus: 'failed',
        statusObj: {
          ...state.statusObj,
          getBookingsForecastCountOutbound: "failed",
        },
      };
    },
    [getAnalyticsStats.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getAnalyticsStats: "loading" },
        analyticsStatsStatus: "loading"

      };
    },
    [getAnalyticsStats.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsStats: processAnalyticsStatsResponse(action.payload),
        status: "succeeded",
        statusObj: { ...state.statusObj, getAnalyticsStats: "succeeded" },
        analyticsStatsStatus: "succeeded"
      };
    },
    [getAnalyticsStats.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getAnalyticsStats: "failed" },
        error: action.error.message,
        analyticsStatsStatus: "failed"

      };
    },
    [getAnalyticsStatsDayComparedToLastWeek.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsStatsDayComparedToLastWeek: "loading",
        },
      };
    },
    [getAnalyticsStatsDayComparedToLastWeek.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsStatsDayComparedToLastWeek: action.payload,
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsStatsDayComparedToLastWeek: "succeeded",
        },
      };
    },
    [getAnalyticsStatsDayComparedToLastWeek.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsStatsDayComparedToLastWeek: "failed",
        },
      };
    },
    [getAnalyticsCountInbound.pending]: (state, action) => {
      return {
        ...state,
        getAnalyticsCountInboundStatus: "loading",
        status: "loading",
        statusObj: { ...state.statusObj, getAnalyticsCountInbound: "loading" },
      };
    },
    [getAnalyticsCountInbound.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountInbound: action.payload,
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInbound: "succeeded",
        },
        analyticsCountInboundStatus: "succeeded",
      };
    },
    [getAnalyticsCountInbound.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        analyticsCountInboundStatus: !action.meta.aborted
          ? "failed"
          : state.status,
        statusObj: { ...state.statusObj, getAnalyticsCountInbound: "failed" },
        status: !action.meta.aborted ? "failed" : state.status,
      };
    },
    [getAnalyticsCountOutbound.pending]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundStatus: "loading",
        status: "loading",
        statusObj: { ...state.statusObj, getAnalyticsCountOutbound: "loading" },
      };
    },
    [getAnalyticsCountOutbound.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountOutbound: action.payload,
        analyticsCountOutboundStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutbound: "succeeded",
        },
      };
    },
    [getAnalyticsCountOutbound.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getAnalyticsCountOutbound: "failed" },
        analyticsCountOutboundStatus: !action.meta.aborted
          ? "failed"
          : state.status,
      };
    },
    [getAnalyticsCountInboundByHour.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHour: "loading",
        },
        analyticsCountInboundByHourStatus: "loading",
      };
    },
    [getAnalyticsCountInboundByHour.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountInboundByHour: action.payload,
        analyticsCountInboundByHourStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHour: "succeeded",
        },
      };
    },
    [getAnalyticsCountInboundByHour.rejected]: (state, action) => {
      return {
        ...state,
        analyticsCountInboundByHourStatus: !action.meta.aborted
          ? "failed"
          : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHour: "failed",
        },
        error: action.error.message,
      };
    },
    [getAnalyticsCountOutboundByHour.pending]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHourStatus: "loading",
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHour: "loading",
        },
      };
    },
    [getAnalyticsCountOutboundByHour.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHour: action.payload,
        analyticsCountOutboundByHourStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHour: "succeeded",
        },
      };
    },
    [getAnalyticsCountOutboundByHour.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHour: "failed",
        },
        analyticsCountOutboundByHourStatus: !action.meta.aborted
          ? "failed"
          : state.status,
      };
    },
    [getAnalyticsCountOutboundByHourNoReduce.pending]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHourStatus: "loading",
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourNoReduce: "loading",
        },
      };
    },
    [getAnalyticsCountOutboundByHourNoReduce.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHourNoReduce: action.payload,
        analyticsCountOutboundByHourStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourNoReduce: "succeeded",
        },
      };
    },
    [getAnalyticsCountOutboundByHourNoReduce.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourNoReduce: "failed",
        },
        analyticsCountOutboundByHourStatus: !action.meta.aborted
          ? "failed"
          : state.status,
      };
    },
    [getAnalyticsCountInboundByHourForForecast.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHourForForecast: "loading",
        },
        analyticsCountInboundByHourStatus: "loading",
      };
    },
    [getAnalyticsCountInboundByHourForForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountInboundByHourForForecast: action.payload,
        analyticsCountInboundByHourStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHourForForecast: "succeeded",
        },
      };
    },
    [getAnalyticsCountInboundByHourForForecast.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        analyticsCountInboundByHourStatus: !action.meta.aborted
          ? "failed"
          : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountInboundByHourForForecast: "failed",
        },
      };
    },
    [getAnalyticsCountOutboundByHourForForecast.pending]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHourStatus: "loading",
        status: "loading",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourForForecast: "loading",
        },
      };
    },
    [getAnalyticsCountOutboundByHourForForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        analyticsCountOutboundByHourForForecast: action.payload,
        analyticsCountOutboundByHourStatus: "succeeded",
        status: "succeeded",
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourForForecast: "succeeded",
        },
      };
    },
    [getAnalyticsCountOutboundByHourForForecast.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        analyticsCountOutboundByHourStatus: !action.meta.aborted
          ? "failed"
          : state.status,
        statusObj: {
          ...state.statusObj,
          getAnalyticsCountOutboundByHourForForecast: "state",
        },
        status: !action.meta.aborted ? "failed" : state.status,
      };
    },
    [getArrivalsForecast.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getArrivalsForecast: "loading" },
        arrivalsForecastStatus: "loading",

      };
    },
    [getArrivalsForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getArrivalsForecast: "succeeded" },
        arrivalsForecast: action.payload,
        arrivalsForecastStatus: "succeeded",
      };
    },
    [getArrivalsForecast.rejected]: (state, action) => {
      return {
        ...state,
        error: action.error.message,
        status: !action.meta.aborted ? "failed" : state.status,
        statusObj: { ...state.statusObj, getArrivalsForecast: "failed" },
        arrivalsForecastStatus: "failed",

      };

    },
    [getArrivalsForecastRange.pending]: (state) => {
      return {
        ...state,
        arrivalsForecastRangeStatus: "loading",
      };
    },
    [getArrivalsForecastRange.fulfilled]: (state, action) => {
      return {
        ...state,
        arrivalsForecastRangeStatus: "succeeded",
        arrivalsForecastRange: action.payload,
      };
    },
    [getArrivalsForecastRange.rejected]: (state, action) => {
      return {
        ...state,
        arrivalsForecastRangeStatus: "failed",
        arrivalsForecastRangeError: action.error.message,
      };
    },
    [getArrivalsCountForecast.pending]: (state, action) => {
      return {
        ...state,
        countStatus: "loading",
      };
    },
    [getArrivalsCountForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        countStatus: "succeeded",
        countForecast: action.payload.totalArrivals,
      };
    },
    [getArrivalsCountForecast.rejected]: (state, action) => {
      return {
        ...state,
        error: !action.meta.aborted ? "failed" : state.error,
        countStatus: !action.meta.aborted ? "failed" : state.countStatus,
      };
    },
    [getDeparturesForecast.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getArrivalsCountForecast: "loading" },
        departuresForecastStatus: "loading",

      };
    },
    [getDeparturesForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getDeparturesForecast: "succeeded" },
        departuresForecast: action.payload,
        departuresForecastStatus: "succeeded",

      };
    },
    [getDeparturesForecast.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.error,
        error: !action.meta.aborted ? action.error.message : state.error,
        // countStatus: !action.meta.aborted ? "failed" : state.countStatus,
        statusObj: { ...state.statusObj, getDeparturesForecast: "failed" },
        departuresForecastStatus: "failed",

      };
    },
    [getDeparturesForecastRange.pending]: (state) => {
      return {
        ...state,
        departuresForecastRangeStatus: "loading",
      };
    },
    [getDeparturesForecastRange.fulfilled]: (state, action) => {
      return {
        ...state,
        departuresForecastRangeStatus: "succeeded",
        departuresForecastRange: action.payload,
      };
    },
    [getDeparturesForecastRange.rejected]: (state, action) => {
      return {
        ...state,
        departuresForecastRangeStatus: "failed",
        departuresForecastRangeError: action.error.message,
      };
    },
    [getDeparturesFlightForecastByDateRange.pending]: (state) => {
      return {
        ...state,
        departuresFlightsForecastRangeStatus: "loading",
      };
    },
    [getDeparturesFlightForecastByDateRange.fulfilled]: (state, action) => {

      return {
        ...state,
        departuresFlightsForecastRangeStatus: "succeeded",
        departuresFlightsForecastRange: action.payload,
      };
    },
    [getDeparturesFlightForecastByDateRange.rejected]: (state, action) => {
      return {
        ...state,
        departuresFlightsForecastRangeStatus: "failed",
        departuresFlightsForecastRangeError: action.error.message,
      };
    },
    [getArrivalsFlightForecastByDateRange.pending]: (state) => {
      return {
        ...state,
        arrivalsFlightsForecastRangeStatus: "loading",
      };
    },
    [getArrivalsFlightForecastByDateRange.fulfilled]: (state, action) => {
      return {
        ...state,
        arrivalsFlightsForecastRangeStatus: "succeeded",
        arrivalsFlightsForecastRange: action.payload,
      };
    },
    [getArrivalsFlightForecastByDateRange.rejected]: (state, action) => {
      return {
        ...state,
        arrivalsFlightsForecastRangeStatus: "failed",
        arrivalsFlightsForecastRangeError: action.error.message,
      };
    },

    [getDeparturesCountForecast.pending]: (state, action) => {
      return {
        ...state,
        countStatus: "loading",
        statusObj: {
          ...state.statusObj,
          getDeparturesCountForecast: "loading",
        },
      };
    },
    [getDeparturesCountForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        countStatus: "succeeded",
        countForecast: action.payload.totalDepartures,
        statusObj: {
          ...state.statusObj,
          getDeparturesCountForecast: "succeeded",
        },
      };
    },
    [getDeparturesCountForecast.rejected]: (state, action) => {
      return {
        ...state,
        error: !action.meta.aborted ? "failed" : state.error,
        countStatus: !action.meta.aborted ? "failed" : state.countStatus,
        statusObj: { ...state.statusObj, getDeparturesCountForecast: "failed" },
      };
    },
    [getBookingBreakdownInbound.pending]: (state, action) => {
      return {
        ...state,
        bookingBreakdownStatus: "loading",
        statusObj: {
          ...state.statusObj,
          getBookingBreakdownInbound: "loading",
        },
      };
    },
    [getBookingBreakdownInbound.fulfilled]: (state, action) => {
      return {
        ...state,
        bookingBreakdownStatus: "succeeded",
        statusObj: {
          ...state.statusObj,
          getBookingBreakdownInbound: "succeeded",
        },
        bookingBreakdownInbound: action.payload,
      };
    },
    [getBookingBreakdownInbound.rejected]: (state, action) => {
      return {
        ...state,
        bookingBreakdownStatus: "failed",
        statusObj: { ...state.statusObj, getBookingBreakdownInbound: "failed" },
        bookingBreakdownError: action.error.message,
      };
    },
    [getBookingBreakdownOutbound.pending]: (state, action) => {
      return {
        ...state,
        statusObj: {
          ...state.statusObj,
          getBookingBreakdownOutbound: "loading",
        },
        bookingBreakdownStatus: "loading",
      };
    },
    [getBookingBreakdownOutbound.fulfilled]: (state, action) => {
      return {
        ...state,
        bookingBreakdownStatus: "succeeded",
        statusObj: {
          ...state.statusObj,
          getBookingBreakdownOutbound: "succeeded",
        },
        bookingBreakdownOutbound: action.payload,
      };
    },
    [getBookingBreakdownOutbound.rejected]: (state, action) => {
      return {
        ...state,
        bookingBreakdownStatus: "failed",
        statusObj: {
          ...state.statusObj,
          getBookingBreakdownOutbound: "failed",
        },
        bookingBreakdownError: action.error.message,
      };
    },
    [getBookingsDueTimes.pending]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesStatus: "loading",
      };
    },
    [getBookingsDueTimes.fulfilled]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesStatus: "succeeded",
        bookingsDueTimes: action.payload,
      };
    },
    [getBookingsDueTimes.rejected]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesStatus: "failed",
        bookingsDueTimesError: action.error.message,
      };
    },
    [getBookingsDueTimesBetweenAirports.pending]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesBetweenAirportsStatus: "loading",
      };
    },
    [getBookingsDueTimesBetweenAirports.fulfilled]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesBetweenAirportsStatus: "succeeded",
        bookingsDueTimesBetweenAirports: action.payload,
      };
    },
    [getBookingsDueTimesBetweenAirports.rejected]: (state, action) => {
      return {
        ...state,
        bookingsDueTimesBetweenAirportsStatus: "failed",
        bookingsDueTimesBetweenAirportsError: action.error.message,
      };
    },

    //  weekly forecast analytics
    [getWeeklyAnalyticsCountInboundByHourForForecast.pending]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountInboundByHourForForecastStatus: "loading",
      };
    },
    [getWeeklyAnalyticsCountInboundByHourForForecast.fulfilled]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountInboundByHourForForecast: action.payload,
        weeklyAnalyticsCountInboundByHourForForecastStatus: "succeeded",
      };
    },
    [getWeeklyAnalyticsCountInboundByHourForForecast.rejected]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountInboundByHourForForecastStatus: "failed",

        weeklyAnalyticsCountInboundByHourForForecastError: action.error.message,
      };
    },

    [getWeeklyAnalyticsCountOutboundByHourForForecast.pending]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountOutboundByHourForForecastStatus: "loading",
      };
    },
    [getWeeklyAnalyticsCountOutboundByHourForForecast.fulfilled]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountOutboundByHourForForecast: action.payload,
        weeklyAnalyticsCountOutboundByHourForForecastStatus: "succeeded",
      };
    },
    [getWeeklyAnalyticsCountOutboundByHourForForecast.rejected]: (
      state,
      action
    ) => {
      return {
        ...state,
        weeklyAnalyticsCountOutboundByHourForForecastStatus: "failed",

        weeklyAnalyticsCountOutboundByHourForForecastError:
          action.error.message,
      };
    },

    [getWeeklyArrivalsForecast.pending]: (state, action) => {
      return {
        ...state,
        weeklyArrivalsForecastStatus: "loading",
      };
    },
    [getWeeklyArrivalsForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        weeklyArrivalsForecast: action.payload,
        weeklyArrivalsForecastStatus: "succeeded",
      };
    },
    [getWeeklyArrivalsForecast.rejected]: (state, action) => {
      return {
        ...state,
        weeklyArrivalsForecastStatus: "failed",

        weeklyArrivalsForecastError: action.error.message,
      };
    },

    [getWeeklyDeparturesForecast.pending]: (state, action) => {
      return {
        ...state,
        weeklyDeparturesForecastStatus: "loading",
      };
    },
    [getWeeklyDeparturesForecast.fulfilled]: (state, action) => {
      return {
        ...state,
        weeklyDeparturesForecast: action.payload,
        weeklyDeparturesForecastStatus: "succeeded",
      };
    },
    [getWeeklyDeparturesForecast.rejected]: (state, action) => {
      return {
        ...state,
        weeklyDeparturesForecastStatus: "failed",

        weeklyDeparturesForecastError: action.error.message,
      };
    },
  },
});
const processAnalyticsStatsResponse = (data) => {
  data["inboundDiff"] =
    ((data.inbound - data.inboundPrevious) / data.inboundPrevious) * 100 || 0;
  data["outboundDiff"] =
    ((data.outbound - data.outboundPrevious) / data.outboundPrevious) * 100 ||
    0;
  data["totalDiff"] =
    ((data.outbound +
      data.inbound -
      (data.outboundPrevious + data.inboundPrevious)) /
      (data.outboundPrevious + data.inboundPrevious)) *
    100 || 0;
  data["total"] = data.outbound + data.inbound || 0;

  const totalCount = {
    WCHR: 0,
    WCHS: 0,
    WCHC: 0,
    WCMP: 0,
    DPNA: 0,
    BLND: 0,
    DEAF: 0,
    MEDA: 0,
    SVAN: 0,
    MAAS: 0,
    LNYD: 0,
  };
  const inboundAssistanceCounts = { ...totalCount };
  const outboundAssistanceCounts = { ...totalCount };

  data.inboundAssistance.forEach((element) => {
    inboundAssistanceCounts[element.key[element.key.length - 1]] +=
      element.value;
    totalCount[element.key[element.key.length - 1]] += element.value;
  });

  data.outboundAssistance.forEach((element) => {
    outboundAssistanceCounts[element.key[element.key.length - 1]] +=
      element.value;
    totalCount[element.key[element.key.length - 1]] += element.value;
  });

  data["assistanceTable"] = Object.keys(totalCount).map((e) => {
    return {
      key: e,
      outbound: outboundAssistanceCounts[e],
      inbound: inboundAssistanceCounts[e],
    };
  });

  data["inboundAssistanceCounts"] = inboundAssistanceCounts;
  data["outboundAssistanceCounts"] = outboundAssistanceCounts;
  data["totalAssistanceCounts"] = totalCount;

  return data;
};

const mondayThisWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const sundayThisWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
};

// gets the next day
const getOffsettedDateString = (base, count, string = false) => {
  const date = new Date(base);
  date.setDate(base.getDate() + count);

  if (string) {
    return date.toUTCString().substring(0, 7);
  } else {
    return date.toISOString().split("T")[0];
  }
};

const getOffsettedHourString = (base, count, string = false) => {
  const date = new Date(base).toISOString().split("T")[0];

  if (string) {
    return (count < 10 ? "0" + count.toString() : count.toString()) + ":00";
  } else {
    return (
      date + "_" + (count < 10 ? "0" + count.toString() : count.toString())
    );
  }
};

export const selectDeparturesArrivalsCount = (state) => {
  if (!state.count.typeSelector) {
    return [];
  }
  if (!state.date.date) {
    return [];
  }
  if (!state.count.analyticsCountOutbound) {
    return [];
  }
  if (!state.count.analyticsCountInbound) {
    return [];
  }

  let inboundData = state.count.analyticsCountInbound;
  let outboundData = state.count.analyticsCountOutbound;

  const thisWeek = (date) => {
    const monday = mondayThisWeek(date);
    return [
      {
        name: getOffsettedDateString(monday, 0, true),
        Departures: outboundData[getOffsettedDateString(monday, 0)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 0)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 1, true),
        Departures: outboundData[getOffsettedDateString(monday, 1)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 1)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 2, true),
        Departures: outboundData[getOffsettedDateString(monday, 2)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 2)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 3, true),
        Departures: outboundData[getOffsettedDateString(monday, 3)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 3)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 4, true),
        Departures: outboundData[getOffsettedDateString(monday, 4)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 4)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 5, true),
        Departures: outboundData[getOffsettedDateString(monday, 5)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 5)] || 0,
      },
      {
        name: getOffsettedDateString(monday, 6, true),
        Departures: outboundData[getOffsettedDateString(monday, 6)] || 0,
        Arrivals: inboundData[getOffsettedDateString(monday, 6)] || 0,
      },
    ];
  };

  return thisWeek(new Date(state.date.date).toISOString().split("T")[0]);
};

function zeroPad(num, places) {
  var zero = places - num.toString().length + 1;
  return Array(+(zero > 0 && zero)).join("0") + num;
}

export const selectAnalyticsForecastStats = (state) => {
  if (!state.count.departuresForecast) {
    return [];
  }
  if (!state.count.arrivalsForecast) {
    return [];
  }

  let inboundData = state.count.arrivalsForecast;
  let outboundData = state.count.departuresForecast;

  let inboundTotal = inboundData.reduce((a, b) => {
    return a + b.value;
  }, 0);

  let outboundTotal = outboundData.reduce((a, b) => {
    return a + b.value;
  }, 0);

  return {
    inbound: Math.round(inboundTotal),
    outbound: Math.round(outboundTotal),
    total: Math.round(outboundTotal + inboundTotal),
  };
};

// data object used in forecasted booking graph
export const selectDeparturesArrivalsCountByHourForecast = (state) => {
  if (!state.count.arrivalsForecast || !state.count.departuresForecast) {
    return [];
  }
  if (!state.count.departuresForecast) {
    return [];
  }

  // get current date
  let date = new Date(state.date.date).toISOString().split("T")[0];

  // use forecast data
  let inboundData = state.count.arrivalsForecast;
  let outboundData = state.count.departuresForecast;

  // console.log('outbound data ', outboundData)

  // create objects to stored mapping
  let inboundMapping = {};
  let outboundMapping = {};

  //console.log('inbound forecast data: ', inboundData)

  // for each booking in inbound data
  for (let i of inboundData) {
    let hour = i.key[2];

    //console.log('i', i);
    //console.log('hour', hour);

    if (inboundMapping[hour]) {
      inboundMapping[hour] += i.value;
    } else {
      inboundMapping[hour] = i.value;
    }
  }

  for (let i of outboundData) {
    let hour = i.key[2];
    if (outboundMapping[hour]) {
      outboundMapping[hour] += i.value;
    } else {
      outboundMapping[hour] = i.value;
    }
  }

  const thisDay = (date) => {
    let result = [];
    for (let hour in range(24)) {
      result.push({
        name: getOffsettedHourString(date, hour, true),
        Departures: Math.round(outboundMapping[zeroPad(hour, 2)]) || 0,
        Arrivals: Math.round(inboundMapping[zeroPad(hour, 2)]) || 0,
      });
    }
    return result;
  };

  return thisDay(date);
};

export const selectDeparturesArrivalsWeeklyCountByHourForecast = (state) => {
  if (
    !state.count.weeklyArrivalsForecast ||
    !state.count.weeklyDeparturesForecast
  ) {
    return {};
  }

  // Initialize mappings for each day
  let dailyMapping = {};

  // Helper function to process either arrivals or departures data
  const processData = (data, type) => {
    data.forEach((dayItem) => {
      if (!dailyMapping[dayItem.day]) {
        dailyMapping[dayItem.day] = { arrivals: {}, departures: {} };
      }
      let mapping = dailyMapping[dayItem.day][type];
      dayItem.analytics.forEach((booking) => {
        let hour = booking.key[2];
        if (mapping[hour]) {
          mapping[hour] += booking.value;
        } else {
          mapping[hour] = booking.value;
        }
      });
    });
  };

  // Process arrivals and departures data
  processData(state.count.weeklyArrivalsForecast, "arrivals");
  processData(state.count.weeklyDeparturesForecast, "departures");

  // Aggregate data by hour for each day
  let results = {};
  Object.keys(dailyMapping).forEach((day) => {
    let hoursData = [];
    for (let hour = 0; hour < 24; hour++) {
      let hourKey = hour.toString().padStart(2, "0");
      hoursData.push({
        name: `${hourKey}:00`,
        Departures: Math.round(dailyMapping[day].departures[hourKey] || 0),
        Arrivals: Math.round(dailyMapping[day].arrivals[hourKey] || 0),
      });
    }
    results[day] = hoursData;
  });

  return results;
};

// this function takes an hour and minute and returns the nearest quarter hour
// eg. 10:11am would return 10:00am
function roundTimeToNearestQuarter(hour, minute) {
  // Calculate the nearest quarter by rounding down
  const nearestQuarter = Math.floor(minute / 15) * 15;

  // Format the hour and minute to ensure two digits
  const formattedHour = hour.toString().padStart(2, "0");
  const formattedMinute = nearestQuarter.toString().padStart(2, "0");

  return `${formattedHour}:${formattedMinute}`;
}

function roundToNextQuarterHour(isoString) {
  // Parse the date string
  const date = new Date(isoString);

  // Get the current hours and minutes
  let hours = date.getHours();
  let minutes = date.getMinutes();

  // Round minutes to the next quarter hour
  if (minutes > 0 && minutes <= 15) {
    minutes = 15;
  } else if (minutes > 15 && minutes <= 30) {
    minutes = 30;
  } else if (minutes > 30 && minutes <= 45) {
    minutes = 45;
  } else if (minutes > 45) {
    minutes = 0;
    hours += 1; // Increment the hour if minutes are rounded past 45
  }

  // Handle the case where hours roll over to the next day
  if (hours === 24) {
    hours = 0;
  }

  // Format hours and minutes as strings with leading zeros if necessary
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');

  // Return the formatted time string
  return `${hoursStr}:${minutesStr}`;
}

// data object used in forecasted booking graph
export const selectDeparturesArrivalsCountByQuarterHourForecast = (state) => {
  if (!state.count.arrivalsForecast || !state.count.departuresForecast) {
    return [];
  }

  // get current date
  let date = new Date(state.date.date).toISOString().split("T")[0];

  // use forecast data
  let inboundData = state.count.arrivalsForecast;
  let outboundData = state.count.departuresForecast;

  //console.log('inbound forecast data: ', inboundData)
  //console.log('raw data', outboundData)

  // mapping function that process each flight and stores the average passenger count in the correct time interval
  const processFlightData = (flightData) => {
    let mappingObject = {};

    flightData.forEach((flight) => {

      // console.log('flight', flight)

      // extract information from the flight forecast recrod
      let hour = flight.key[2];
      let minute = flight.key[7];
      let average = flight.value;
      let isoString = flight.key[6];

      // find which interval the flight belongs to
      // let interval = roundToNextQuarterHour(hour, minute);
      let interval = roundToNextQuarterHour(isoString);

      // console.log('interval: ', interval)

      // add the average to the interval in the mapping object
      if (mappingObject[interval]) {
        mappingObject[interval] += average;
      } else {
        mappingObject[interval] = average;
      }
    });

    return mappingObject;
  };

  // create objects to stored mapping
  let inboundMapping = processFlightData(inboundData);
  let outboundMapping = processFlightData(outboundData);

  // console.log('outbound mapping: ', outboundMapping);
  // console.log('inbound mapping: ', inboundMapping);

  // create data object for the graph
  let graphData = [];

  /* for each 15 min interval in an hour in a day */
  for (let i = 0; i < 24 * 4; i++) {
    // calculate interval key using hour + minute
    let hour = Math.floor(i / 4);
    let minute = (i % 4) * 15;
    let intervalKey = zeroPad(hour, 2) + ":" + zeroPad(minute, 2);

    // add data for this interval
    graphData.push({
      name: intervalKey,
      Departures: Math.round(outboundMapping[zeroPad(intervalKey, 2)]) || 0,
      Arrivals: Math.round(inboundMapping[zeroPad(intervalKey, 2)]) || 0,
    });
  }

  //console.log('graph data: ', graphData);

  return graphData;
};

export const selectDeparturesArrivalsCountByHourForecastAdjusted = (state) => {
  if (!state.count.arrivalsForecast) {
    return [];
  }
  if (!state.count.departuresForecast) {
    return [];
  }

  let date = new Date(state.date.date).toISOString().split("T")[0];

  let inboundData = state.count.arrivalsForecast;
  let outboundData = generateOutboundData(
    state.count.departuresForecast,
    state.reviews.averageDueTime,
    6
  );

  let inboundMapping = {};

  for (let i of inboundData) {
    let string = i.key[i.key.length - 5];
    if (inboundMapping[string]) {
      inboundMapping[string] += i.value;
    } else {
      inboundMapping[string] = i.value;
    }
  }

  const thisDay = (date) => {

    let result = [];

    for (let hour in range(24)) {
      result.push({
        name: getOffsettedHourString(date, hour, true),
        Departures:
          Math.floor(outboundData[getOffsettedHourString(date, hour)]) || 0,
        Arrivals:
          Math.floor(
            inboundMapping[
            parseInt(hour) < 10 ? "0" + hour.toString() : hour.toString()
            ]
          ) || 0,
      });
    }

    return result;
  };

  let returnVal = thisDay(
    new Date(state.date.date).toISOString().split("T")[0]
  );

  return returnVal;
};

export const selectDeparturesArrivalsCountByHour = (state) => {
  if (!state.count.typeSelector) {
    return [];
  }
  if (!state.date.date) {
    return [];
  }
  if (!state.count.analyticsCountOutboundByHour) {
    return [];
  }
  if (!state.count.analyticsCountInboundByHour) {
    return [];
  }

  let inboundData = state.count.analyticsCountInboundByHour;
  let outboundData = state.count.analyticsCountOutboundByHour;

  //console.log("outbound data ", outboundData);

  const thisDay = (date) => {
    let result = [];

    for (let hour in range(24)) {
      result.push({
        name: getOffsettedHourString(date, hour, true),
        Departures: outboundData[getOffsettedHourString(date, hour)] || 0,
        Arrivals: inboundData[getOffsettedHourString(date, hour)] || 0,
      });
    }

    return result;
  };

  let returnVal = thisDay(
    new Date(state.date.date).toISOString().split("T")[0]
  );

  return returnVal;
};

export const selectDeparturesArrivalsCountByQuarter = (state) => {
  //console.log('called', state.count.inboundDocuments)

  // {name: '00:00', Departures: 0, Arrivals: 9}
  if (state.count.inboundDocuments == null || state.count.inboundDocuments == undefined || !Array.isArray(state.count.inboundDocuments)) {
    return [];
  }

  if (state.count.outboundDocuments == null || state.count.outboundDocuments == undefined || !Array.isArray(state.count.outboundDocuments)) {
    return [];
  }

  let inboundList = [];
  let inboundData = state.count.inboundDocuments;

  //console.log('inbound data ', inboundData)

  inboundData.forEach((bookingDoc) => {
    let booking = bookingDoc.doc

    if (booking) {
      // add booking to list using the hour and minute of arrival
      let arrivalTime = booking.flightConfirmationDetails.arrivalTime

      if (arrivalTime != undefined && arrivalTime != null) {

        // need to quarter from arrival time
        let quarter = roundToNextQuarterHour(arrivalTime)

        // check if the quarter is already in the list
        if (inboundList[quarter]) {

          // add the booking to the list
          inboundList[quarter] += 1

        }
        else {
          // create a new entry in the list
          inboundList[quarter] = 1
        }
      }
    }
  })

  //console.log('outboundDocuments', state.count.outboundDocuments)

  let outboundList = [];
  let outboundData = state.count.outboundDocuments;

  outboundData.forEach((bookingDoc) => {
    let booking = bookingDoc.doc

    if (booking) {
      // add booking to list using the hour and minute of arrival
      let departureTime = booking.flightConfirmationDetails.departureTime

      if (departureTime != undefined && departureTime != null) {

        // need to quarter from arrival time
        let quarter = roundToNextQuarterHour(departureTime)

        // check if the quarter is already in the list
        if (outboundList[quarter]) {

          // add the booking to the list
          outboundList[quarter] += 1

        }
        else {
          // create a new entry in the list
          outboundList[quarter] = 1
        }
      }
    }
  })


  // console.log('outboundList list', outboundList)

  // combine lists and return them in one list
  let combinedList = []

  for (let i = 0; i < 24 * 4; i++) {
    // calculate interval key using hour + minute
    let hour = Math.floor(i / 4);
    let minute = (i % 4) * 15;
    let intervalKey = zeroPad(hour, 2) + ":" + zeroPad(minute, 2);

    // add data for this interval
    combinedList.push({
      name: intervalKey,
      Departures: Math.round(outboundList[zeroPad(intervalKey, 2)]) || 0,
      Arrivals: Math.round(inboundList[zeroPad(intervalKey, 2)]) || 0,
    });
  }

  return combinedList

}

export const selectDeparturesArrivalsCountByQuarterAdjustedDueTime = (state) => {
  if (state.count.inboundDocuments == null || state.count.inboundDocuments == undefined || !Array.isArray(state.count.inboundDocuments)) {
    return [];
  }

  if (state.count.outboundDocuments == null || state.count.outboundDocuments == undefined || !Array.isArray(state.count.outboundDocuments)) {
    return [];
  }

  const averageDueTimes = getDueTimesAverageHour(state?.count?.bookingsDueTimes);

  let inboundList = [];
  let inboundData = state.count.inboundDocuments;

  inboundData.forEach((bookingDoc) => {
    let booking = bookingDoc.doc;

    if (booking) {
      let arrivalTime = booking.flightConfirmationDetails.arrivalTime;

      if (arrivalTime != undefined && arrivalTime != null) {
        let quarter = roundToNextQuarterHour(arrivalTime);

        if (inboundList[quarter]) {
          inboundList[quarter] += 1;
        } else {
          inboundList[quarter] = 1;
        }
      }
    }
  });

  let outboundList = [];
  let outboundData = state.count.outboundDocuments;

  outboundData.forEach((bookingDoc) => {
    let booking = bookingDoc.doc;

    if (booking) {
      let departureTime = booking.flightConfirmationDetails.departureTime;

      if (departureTime != undefined && departureTime != null) {
        const date = new Date(departureTime);
        let hours = date.getHours();
        let minutes = date.getMinutes();

        // Subtract due time offset (in hours) based on destination airport
        let destination = booking.flightConfirmationDetails.destination;
        let dueTimeHours = averageDueTimes[destination] || 1;
        hours = hours - dueTimeHours;
        if (hours < 0) hours = 0;

        // Rebuild an ISO string with adjusted hour for quarter bucketing
        const adjusted = new Date(date);
        adjusted.setHours(hours, minutes, 0, 0);
        let quarter = roundToNextQuarterHour(adjusted.toISOString());

        if (outboundList[quarter]) {
          outboundList[quarter] += 1;
        } else {
          outboundList[quarter] = 1;
        }
      }
    }
  });

  let combinedList = [];

  for (let i = 0; i < 24 * 4; i++) {
    let hour = Math.floor(i / 4);
    let minute = (i % 4) * 15;
    let intervalKey = zeroPad(hour, 2) + ":" + zeroPad(minute, 2);

    combinedList.push({
      name: intervalKey,
      Departures: Math.round(outboundList[zeroPad(intervalKey, 2)]) || 0,
      Arrivals: Math.round(inboundList[zeroPad(intervalKey, 2)]) || 0,
    });
  }

  return combinedList;
}

const generateOutboundData = (bookingList, averageDueTimes, keyIndex) => {
  if (bookingList == undefined || bookingList == null) {
    return [];
  }
  if (averageDueTimes == undefined || averageDueTimes == null) {
    return [];
  }

  if (bookingList == undefined || bookingList == null) { return [] }
  if (averageDueTimes == undefined || averageDueTimes == null) { return [] }

  let averageDueTime = Object.values(averageDueTimes).map(e => e.sum / e.count).reduce((a, b) => a + b, 0) / Object.values(averageDueTimes).length

  let reducedBookingList = bookingList.reduce((object, time) => {
    let hour = parseInt(time.key[2])
    try {
      // default time if not available
      let hourDif = -3;
      try {
        let sum = averageDueTimes[time.key[keyIndex]].sum
        let count = averageDueTimes[time.key[keyIndex]].count
        hourDif = Math.floor((sum / count) / (1000 * 60 * 60))
      } catch (e) {

      }

      if (hour && hourDif) {
        hour = (hour + hourDif).toString()
        // if single digit add leading 0
        if (hour.length == 1) {
          hour = '0' + hour
        }
      } else {
        hour = time.key[2];
      }
      let date_string = time.key[1] + '_' + hour
      if (object[date_string]) {
        object[date_string] += time.value
      } else {
        object[date_string] = time.value;
      }
    } catch (e) {
    }
    return object
  }, {})

  return reducedBookingList

}

// get the average of bookings due timees in hourse
const getDueTimesAverageHour = (dueTimes) => {
  if (dueTimes !== null && dueTimes?.dueTimeCount !== undefined && dueTimes?.dueTimeSum !== undefined) {
    if (
      Object?.keys(dueTimes?.dueTimeCount)?.length < 0 &&
      Object?.keys(dueTimes?.dueTimeSum)?.length < 0
    ) {
      return {};
    }
    let AverageObj = {};
    // map throw the dueTimes and divide the sum for dueTimes for each airport on the count to get the average in miliseconds then divide on (1000 * 60 * 60) to get the average in hours
    Object.keys(dueTimes?.dueTimeCount)?.map((airport) => {
      AverageObj[airport] = Math.floor(
        dueTimes?.dueTimeSum[airport] /
        dueTimes?.dueTimeCount[airport] /
        (1000 * 60 * 60)
      );
    });
    return AverageObj;
  } else return {};
};

export const selectDeparturesArrivalsCountByHourAdjustedDueTime = (state) => {
  if (
    !state.count.typeSelector ||
    !state.date.date ||
    !state.count.analyticsCountOutboundByHour ||
    !state.count.analyticsCountInboundByHour ||
    !state.count.analyticsCountOutboundByHourNoReduce ||
    !state.reviews.averageDueTime
  ) {
    return [];
  }

  let inboundData = state.count.analyticsCountInboundByHour;
  // let outboundData = generateOutboundData(
  //   state.count.analyticsCountOutboundByHourNoReduce,
  //   state.reviews.averageDueTime,
  //   4
  // );

  // get the analytics for OutboundData in real time
  const getOutboundDataWithaverageDueTimes = (
    bookingList,
    averageDueTimes,
    keyIndex
  ) => {
    if (bookingList == undefined || bookingList == null) {
      return [];
    }
    if (averageDueTimes == undefined || averageDueTimes == null) {
      return [];
    }
    let reducedBookingList = bookingList.reduce((object, time) => {
      let hour = parseInt(time.key[2]);
      try {
        // default time of passenger arriving to airport is set to one hour
        let passengerDueTime = 1;
        try {
          passengerDueTime = averageDueTimes[time.key[keyIndex]];
        } catch (e) { }
        if (hour && passengerDueTime) {
          hour = (hour - passengerDueTime).toString();
          // if single digit add leading 0
          if (hour.length == 1) {
            hour = "0" + hour;
          }
        } else {
          hour = time.key[2];
        }
        let date_string = time.key[1] + "_" + hour;
        if (object[date_string]) {
          object[date_string] += time.value;
        } else {
          object[date_string] = time.value;
        }
      } catch (e) { }
      return object;
    }, {});

    return reducedBookingList;
  };
  let outboundData = getOutboundDataWithaverageDueTimes(
    state.count.analyticsCountOutboundByHourNoReduce,
    getDueTimesAverageHour(state?.count?.bookingsDueTimes),
    4
  );
  const thisDay = (date) => {

    let result = [];

    for (let hour in range(24)) {
      result.push({
        name: getOffsettedHourString(date, hour, true),
        Departures: outboundData[getOffsettedHourString(date, hour)] || 0,
        Arrivals: inboundData[getOffsettedHourString(date, hour)] || 0,
      });
    }

    return result;
  };

  let returnVal = thisDay(
    new Date(state.date.date).toISOString().split("T")[0]
  );

  return returnVal;
};

export const selectMaxPage = (type) => (state) => {
  if (!state.count.typeSelector) {
    return [];
  }
  if (!state.date.date) {
    return [];
  }
  if (!state.count.analyticsCountOutboundByHour) {
    return [];
  }
  if (!state.count.analyticsCountInboundByHour) {
    return [];
  }
  if (!state.count.analyticsStats || !state.count.analyticsStats.total) {
    return [];
  }

  let dataDeparture = { ...state.count.analyticsCountOutboundByHour };
  let dataArrival = { ...state.count.analyticsCountInboundByHour };

  const thisDay = () => {
    let result = [];

    for (let d in dataDeparture) {
      if (dataDeparture[d] > state.count.analyticsStats.total / 100)
        result.push({
          name:
            d.split("_")[1] +
            "h-" +
            (parseInt(d.split("_")[1]) + 1).toString() +
            "h " +
            d.split("_")[2],
          "Number of bookings (Departure)": dataDeparture[d] || 0,
          "Number of bookings (Arrival)": 0,
        });
    }
    for (let d in dataArrival) {
      if (dataArrival[d] > state.count.analyticsStats.total / 100)
        result.push({
          name:
            d.split("_")[1] +
            "h-" +
            (parseInt(d.split("_")[1]) + 1).toString() +
            "h " +
            d.split("_")[2],
          "Number of bookings (Departure)": 0,
          "Number of bookings (Arrival)": dataArrival[d] || 0,
        });
    }
    return result;
  };

  let data = thisDay();
  if (!data) return 0;
  let result = Object.keys(data).length / 10 - 1 || 0;
  if (result > Math.floor(result)) {
    return Math.floor(result) + 1;
  } else {
    return Math.floor(result);
  }
};

export const selectDeparturesArrivalsCountByFlight =
  (type, page) => (state) => {
    if (!state.count.typeSelector) {
      return [];
    }
    if (!state.date.date) {
      return [];
    }
    if (!state.count.analyticsCountOutboundByHour) {
      return [];
    }
    if (!state.count.analyticsCountInboundByHour) {
      return [];
    }
    if (!state.count.analyticsStats) {
      return [];
    }

    let dataDeparture = { ...state.count.analyticsCountOutboundByHour };
    let dataArrival = { ...state.count.analyticsCountInboundByHour };

    const thisDay = (page) => {
      let result = [];

      for (let d in dataDeparture) {
        if (dataDeparture[d] > state.count.analyticsStats.total / 100)
          result.push({
            name:
              d.split("_")[1] +
              "h-" +
              (parseInt(d.split("_")[1]) + 1).toString() +
              "h " +
              d.split("_")[2],
            "Number of bookings (Departure)": dataDeparture[d] || 0,
            "Number of bookings (Arrival)": 0,
          });
      }
      for (let d in dataArrival) {
        if (dataArrival[d] > state.count.analyticsStats.total / 100)
          result.push({
            name:
              d.split("_")[1] +
              "h-" +
              (parseInt(d.split("_")[1]) + 1).toString() +
              "h " +
              d.split("_")[2],
            "Number of bookings (Departure)": 0,
            "Number of bookings (Arrival)": dataArrival[d] || 0,
          });
      }
      result.sort((a, b) => {
        return a["name"] > b["name"] ? -1 : +1;
      });
      return result
        .slice(
          result.length - 10 * (page + 1) >= 0
            ? result.length - 10 * (page + 1)
            : 0,
          result.length - 10 * page
        )
        .reverse();
    };

    return thisDay(page);
  };

export const selectTotalCount = (state) => {
  if (state.count.countInbound == null) {
    return 0;
  }
  if (state.count.countOutbound == null) {
    return 0;
  }

  let inboundKeys = Object.keys(state.count.countInbound);
  let outboundKeys = Object.keys(state.count.countOutbound);

  let inboundCount = 0;
  let outboundCount = 0;

  // inbound count
  for (let index = 0; index < inboundKeys.length; index++) {
    if (state.count.countInbound[inboundKeys[index]]) {
      inboundCount = inboundCount + state.count.countInbound[inboundKeys[index]];
    }
  }

  // outbound count
  for (let index = 0; index < outboundKeys.length; index++) {
    if (state.count.countInbound[outboundKeys[index]]) {
      outboundCount =
        outboundCount + state.count.countInbound[outboundKeys[index]];
    }
  }

  return Math.round(inboundCount + outboundCount).toLocaleString();
};

export const selectLowestAndHighestDay = (date) => (state) => {
  date = new Date(new Date(date.getFullYear(), date.getMonth() + 1, 0));

  if (
    !state.count.countInbound ||
    !state.count.countOutbound ||
    !state.count.countOutboundForecast ||
    !state.count.countInboundForecast) return null

  let result = {};

  try {
    for (let i = 0; i < 5; i++) {
      let sunday = sundayThisWeek(new Date(new Date(date.getFullYear(), date.getMonth() + 1, 0).setDate(date.getDate() - (7 * i))));

      let leastBusyNumber = Number.MAX_VALUE;
      let leastBusyDay = null;

      let mostBusyNumber = 0;
      let mostBusyDay = null;

      for (let j = 0; j < 7; j++) {
        let total = 0
        let day = new Date(new Date(sunday).setDate(sunday.getDate() + j)).toISOString().split('T')[0];
        if (day >= new Date().toISOString()) {
          total = state.count.countInboundForecast[day] + state.count.countOutboundForecast[day];
        } else {
          total =
            state.count.countInbound[day] + state.count.countOutbound[day];
        }

        // total = state.count.countInboundForecast[day] + state.count.countOutboundForecast[day];

        if (total < leastBusyNumber) {
          leastBusyNumber = total;
          leastBusyDay = day;
        }

        if (total > mostBusyNumber) {
          mostBusyNumber = total;
          mostBusyDay = day;
        }
      }

      result[i] = { leastBusyDay, mostBusyDay, sunday };
    }

    return result;
  } catch (e) {
    return null;
  }
};

export const rankDaysOfWeekByBookings = (date) => (state) => {
  // get last day of month
  date = new Date(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  // null checkers
  if (!state.count.countInboundForecast) return null;
  if (!state.count.countOutboundForecast) return null;
  if (!state.count.countInbound) return null;
  if (!state.count.countOutbound) return null;

  let result = {};

  // gets total number of bookings for the jth day after sunday
  const getTotalBookings = (sunday, j) => {
    let total = 0;
    let day = new Date(new Date(sunday).setDate(sunday.getDate() + j))
      .toISOString()
      .split("T")[0];
    if (day >= new Date().toISOString()) {
      total =
        state.count.countInboundForecast[day] +
        state.count.countOutboundForecast[day];
    } else {
      total = state.count.countInbound[day] + state.count.countOutbound[day];
    }
    return total;
  };

  try {
    // iterate through each week of the month
    for (let i = 0; i < 5; i++) {

      // find sunday date
      let sunday = sundayThisWeek(new Date(new Date(date.getFullYear(), date.getMonth() + 1, 0).setDate(date.getDate() - (7 * i))));

      // create an object list of each date in the week from sunday onwards
      let days = [...Array(7).keys()].map(
        j => new Date(new Date(sunday).setDate(sunday.getDate() + j)).toISOString().split('T')[0]
      )

      // map total bookings 
      let num_bookings = [...Array(7).keys()].map(j => getTotalBookings(sunday, j))

      // map total bookings for each day of week in the week[index]
      result[i] = Object.assign(...days.map((k, j) => ({ [k]: num_bookings[j] })))
    }

    return result

    return result;
  } catch (e) {
    return null;
  }
};

function createDateFromHourAndMinute(hour, minute) {
  const date = new Date(); // This creates a Date object with the current date and time
  date.setHours(hour, minute, 0, 0); // Sets the time to the specified hour and minute, with 0 seconds and 0 milliseconds
  return date;
}

export const selectArrivalsForecastList = (state) => {
  // copy the inbound flight data w/ actual counts
  let inboundFlightData = {};
  for (let k in state.count.analyticsCountInboundByHourForForecast) {
    // need to remove the date from the key
    inboundFlightData[k.split("_")[2]] =
      state.count.analyticsCountInboundByHourForForecast[k];
  }

  // initalize list that will hold the forecasted arrivals
  let list = [];

  // console.log('arrivals forecast' , state.count.arrivalsForecast)

  for (let indexCounter in state.count.arrivalsForecast) {
    let booking = state.count.arrivalsForecast[indexCounter];

    // extract variables from booking
    let id = booking.id;
    let departureAirport = booking.key[5];
    let arrivalAirport = booking.key[6];
    let flight = booking.key[3];
    let arrivalTime = new Date(booking.key[6])
    let options = { hour: '2-digit', minute: '2-digit', hour12: false };
    let time = arrivalTime.toLocaleTimeString('en-GB', options);

    let average = booking.value;

    let formattedTime = ""

    if (arrivalTime && arrivalTime != null && arrivalTime != 'Invalid Date') {
      formattedTime = arrivalTime?.toISOString()?.split('T')[0]; // created formatted time used for sorting list in order of time
    }
    else {
      formattedTime = arrivalTime
    }

    // find actual using flight info + inbound flight data
    let actual = inboundFlightData[flight] || 0;

    // calculate variance
    let variance = average - actual;

    list.push({
      count: indexCounter,
      flight: flight,
      time: time,
      formattedTime: formattedTime,
      average: average ? average.toFixed(1) : 0,
      actual: actual,
      variance: variance ? variance.toFixed(1) : 0,
      id: id,
    });
  }

  // console.log('list:', list)

  let timeFormattedList = list.sort(
    (a, b) => a.formattedTime - b.formattedTime
  );

  return timeFormattedList;
};
export const selectArrivalsFlightForecastData = (state) => {
  // copy the inbound flight data w/ actual counts
  let inboundFlightData = {};
  for (let k in state.count.analyticsCountInboundByHourForForecast) {
    // need to remove the date from the key
    inboundFlightData[k.split("_")[2]] =
      state.count.analyticsCountInboundByHourForForecast[k];
  }

  // initalize list that will hold the forecasted arrivals
  let list = [];
  const arrivalFlightsForecast = state.count.arrivalsFlightsForecastRange?.bookings
  for (let indexCounter in arrivalFlightsForecast) {
    let booking = arrivalFlightsForecast[indexCounter];

    // extract variables from booking
    let id = booking.id;
    let departureAirport = booking.key[5];
    let arrivalAirport = booking.key[6];
    let flight = booking.key[3];
    let arrivalTime = new Date(booking.key[6])
    let options = { hour: '2-digit', minute: '2-digit', hour12: false };
    let time = arrivalTime.toLocaleTimeString('en-GB', options);

    let average = booking.value;

    let formattedTime = ""

    if (arrivalTime && arrivalTime != null && arrivalTime != 'Invalid Date') {
      formattedTime = arrivalTime?.toISOString()?.split('T')[0]; // created formatted time used for sorting list in order of time
    }
    else {
      formattedTime = arrivalTime
    }

    // find actual using flight info + inbound flight data
    let actual = inboundFlightData[flight] || 0;

    // calculate variance
    let variance = average - actual;
    list.push({
      count: indexCounter,
      flight: flight,
      time: time,
      formattedTime: formattedTime,
      average: average ? average.toFixed(1) : 0,
      actual: actual,
      variance: variance ? variance.toFixed(1) : 0,
      id: id,
    });
  }

  // console.log('list:', list)

  let timeFormattedList = list.sort(
    (a, b) => a.formattedTime - b.formattedTime
  );

  return timeFormattedList;
};

export const selectWeeklyArrivalsForecastList = (state) => {
  // Organize inbound flight data by day
  let inboundFlightData = {};
  state.count.weeklyAnalyticsCountInboundByHourForForecast?.forEach((item) => {
    inboundFlightData[item?.day] = {};
    Object?.keys(item.analytics)?.forEach((key) => {
      inboundFlightData[item?.day][key?.split("_")[2]] = item?.analytics[key];
    });
  });
  // Initialize a dictionary to hold the forecasted arrivals separated by day
  let listByDay = {};

  // Iterate over each day's forecast
  state.count.weeklyArrivalsForecast?.forEach((dayItem) => {
    listByDay[dayItem.day] = []; // Initialize an array for each day

    dayItem.analytics?.forEach((booking) => {
      // Extract variables from booking
      let id = booking.id;
      let flight = booking.key[3];
      let time = booking.key[2] + ":" + booking.key[7];
      let average = booking.value;
      let formattedTime = createDateFromHourAndMinute(
        booking.key[2],
        booking.key[7]
      ); // Created formatted time used for sorting list in order of time

      // Find actual count using flight info + inbound flight data
      let actual = inboundFlightData?.[dayItem?.day]?.[flight] || 0;

      // Calculate variance
      let variance = average - actual;

      listByDay[dayItem.day].push({
        flight: flight,
        time: time,
        formattedTime: formattedTime,
        average: average ? average.toFixed(1) : 0,
        actual: actual,
        variance: variance ? variance.toFixed(1) : 0,
        id: id,
      });
    });

    // Sort list for the day in order of formatted time
    listByDay[dayItem.day].sort((a, b) => a.formattedTime - b.formattedTime);
  });

  return listByDay;
};

export const selectFlightCountAnalytics = (state) => {
  let inboundList = state.count.analyticsCountInboundByHour;
  let outboundList = state.count.analyticsCountOutboundByHour;
  let departureCount = 0;
  let arrivalCount = 0;
  let totalCount = 0;

  // count each flight in the inbound list
  for (let indexCounter in inboundList) {
    arrivalCount += inboundList[indexCounter];
  }

  // count each flight in the outbound list
  for (let indexCounter in outboundList) {
    departureCount += outboundList[indexCounter];
  }

  // create object to store the counts
  return {
    arrivalCount: arrivalCount,
    departureCount: departureCount,
    totalCount: arrivalCount + departureCount,
  };
};

export const selectDepartureFlightForecast = (state) => {
  return 0;
};

export const selectDepartureTimelineData = (state) => {
  //console.log('here: ', state.count.departuresForecast);

  if (state.count.departuresForecast == null) {
    return;
  }

  let departureFlights = [...state.count.departuresForecast];
  let flightDictionary = {};

  departureFlights.forEach((flight) => {
    // get the hour and minute for the flight
    let hour = flight.key[2];
    let minute = flight.key[7];

    // create key using hour and minute
    let key = hour + ":" + minute;

    if (flightDictionary[key]) {
      flightDictionary[key].push(flight);
    } else {
      flightDictionary[key] = [flight];
    }
  });

  //console.log('flight dictionary: ', flightDictionary);

  return flightDictionary;
};

export const selectDepartureFlightsCount = (state) => {
  if (state.count.departureForecast != null) {
    return state.count.departuresForecast.length;
  } else {
    return 0;
  }
};

export const selectArrivalFlightsCount = (state) => {
  if (state.count.departureForecast != null) {
    return state.count.arrivalsForecast.length;
  } else {
    return 0;
  }
};

export const selectDepartureForecastList = (state) => {
  // copy the inbound flight data w/ actual counts
  let inboundFlightData = {};
  for (let k in state.count.analyticsCountOutboundByHourForForecast) {
    // need to remove the date from the key
    inboundFlightData[k.split("_")[2]] =
      state.count.analyticsCountOutboundByHourForForecast[k];
  }

  let list = [];
  for (let indexCounter in state.count.departuresForecast) {
    let booking = state.count.departuresForecast[indexCounter];


    // extract variables from booking
    let id = booking.id;
    let departureAirport = booking.key[4];
    let arrivalAirport = booking.key[5];
    let flight = booking.key[3];
    let departureTime = new Date(booking.key[6])
    let options = { hour: '2-digit', minute: '2-digit', hour12: false };
    let time = departureTime.toLocaleTimeString('en-GB', options);

    let average = booking.value;

    let formattedTime = ""

    if (departureTime && departureTime != null && departureTime != 'Invalid Date') {
      formattedTime = departureTime.toISOString().split('T')[0]
    }
    else { formattedTime = departureTime }

    // find actual using flight info + inbound flight data
    let actual = inboundFlightData[flight] || 0;

    // calculate variance
    let variance = average - actual;

    let noShowAverage = booking.key[7] ? booking.key[7] : 0;

    list.push({
      count: indexCounter,
      flight: flight,
      time: time,
      formattedTime: formattedTime,
      average: average ? average.toFixed(1) : 0,
      actual: actual,
      variance: variance ? variance.toFixed(1) : 0,
      id: id,
      noShowAverage,
      noShowAverage,
    });
  }

  // console.log('list:', list)

  let timeFormattedList = list.sort(
    (a, b) => a.formattedTime - b.formattedTime
  );

  return timeFormattedList;
};
export const selectDepartureFlightForecastData = (state) => {
  // copy the inbound flight data w/ actual counts
  let inboundFlightData = {};
  for (let k in state.count.analyticsCountOutboundByHourForForecast) {
    // need to remove the date from the key
    inboundFlightData[k.split("_")[2]] =
      state.count.analyticsCountOutboundByHourForForecast[k];
  }

  let list = [];
  const departuresForecastFlights = state.count.departuresFlightsForecastRange?.bookings
  for (let indexCounter in departuresForecastFlights) {
    let booking = departuresForecastFlights[indexCounter];


    // extract variables from booking
    let id = booking.id;
    let departureAirport = booking.key[4];
    let arrivalAirport = booking.key[5];
    let flight = booking.key[3];
    let departureTime = new Date(booking.key[6])
    let options = { hour: '2-digit', minute: '2-digit', hour12: false };
    let time = departureTime.toLocaleTimeString('en-GB', options);

    let average = booking.value;

    let formattedTime = ""

    if (departureTime && departureTime != null && departureTime != 'Invalid Date') {
      formattedTime = departureTime.toISOString().split('T')[0]
    }
    else { formattedTime = departureTime }

    // find actual using flight info + inbound flight data
    let actual = inboundFlightData[flight] || 0;

    // calculate variance
    let variance = average - actual;

    let noShowAverage = booking.key[7] ? booking.key[7] : 0;

    list.push({
      count: indexCounter,
      flight: flight,
      time: time,
      formattedTime: formattedTime,
      average: average ? average.toFixed(1) : 0,
      actual: actual,
      variance: variance ? variance.toFixed(1) : 0,
      id: id,
      noShowAverage,
      noShowAverage,
    });
  }

  // console.log('list:', list)

  let timeFormattedList = list.sort(
    (a, b) => a.formattedTime - b.formattedTime
  );

  return timeFormattedList;
};

export const selectWeeklyDepartureForecastList = (state) => {
  // Organize inbound flight data by day
  let outboundFlightData = {};
  state.count.weeklyAnalyticsCountOutboundByHourForForecast?.forEach((item) => {
    outboundFlightData[item?.day] = {};
    Object?.keys(item?.analytics)?.forEach((key) => {
      outboundFlightData[item?.day][key?.split("_")[2]] = item?.analytics[key];
    });
  });
  // Initialize a dictionary to hold the forecasted arrivals separated by day
  let listByDay = {};

  // Iterate over each day's forecast
  state.count.weeklyDeparturesForecast?.forEach((dayItem) => {
    listByDay[dayItem?.day] = []; // Initialize an array for each day

    dayItem?.analytics?.forEach((booking) => {
      // Extract variables from booking
      let id = booking.id;
      let flight = booking.key[3];
      let time = booking.key[2] + ":" + booking.key[7];
      let average = booking.value;
      let formattedTime = createDateFromHourAndMinute(
        booking.key[2],
        booking.key[7]
      ); // Created formatted time used for sorting list in order of time

      let actual = outboundFlightData?.[dayItem?.day]?.[flight] || 0;

      // Calculate variance
      let variance = average - actual;

      listByDay[dayItem.day].push({
        flight: flight,
        time: time,
        formattedTime: formattedTime,
        average: average ? average.toFixed(1) : 0,
        actual: actual,
        variance: variance ? variance.toFixed(1) : 0,
        id: id,
      });
    });

    // Sort list for the day in order of formatted time
    listByDay[dayItem.day].sort((a, b) => a.formattedTime - b.formattedTime);
  });

  return listByDay;
};


// forecasted arrivals
export const selectArrivalsForecast = (state) => state.count.arrivalsForecast;

// forecasted departures
export const selectDeparturesForecast = (state) => {
  // console.log('forcast ', state.count.departuresForecast)
  state.count.departuresForecast;
}

export const selectDeparturesCountForecast = (state) =>
  state.count.countForecast;
export const selectArrivalsCountForecast = (state) => state.count.countForecast;
export const {
  setTypeSelector,
  setRange,
  reset,
  setShowDepartures,
  setShowArrivals,
  setShowForcast,
  setShowTotals,
  setShowHighlights,
  setShowWeather,
  resetForecastBookingsRange,
  resetForecastStatus,
  resetAnalyticsStatsStatus,
  resetForecastFlights
} = bookingsCountSlice.actions;
export const selectCountStatus = (state) => state.count.status;

export const selectCountOutbound = (state) => {
  return state.count.countOutbound;
};

export const selectCountInbound = (state) => {
  return state.count.countInbound;
};

export const selectTotalCountInbound = (state) => {
  if (state.count.countInbound == null) {
    return 0;
  }

  let keys = Object.keys(state.count.countInbound);
  let total = 0;
  for (let index = 0; index < keys.length; index++) {
    total = total + state.count.countInbound[keys[index]];
  }
  return total;
};

export const selectTotalCountOutbound = (state) => {
  if (state.count.countOutbound == null) {
    return 0;
  }

  let keys = Object.keys(state.count.countOutbound);
  let total = 0;
  for (let index = 0; index < keys.length; index++) {
    total = total + state.count.countOutbound[keys[index]];
  }
  return total;
};


export const selectPrenotifiedGraphData = (state) => {
  if (state.count.nonprenotifiedCount == undefined || state.count.prenotifiedCount == undefined || state.count.nonprenotifiedCount == null || state.count.prenotifiedCount == null) {
    return [];
  }

  let keys = Object.keys(state.count.nonprenotifiedCount);

  let object = [
    {
      type: "Pre-notified Bookings",
      bookings: state.count.prenotifiedCount[keys[0]],
    },
    {
      type: "Non Pre-notified Bookings",
      bookings: state.count.nonprenotifiedCount[keys[0]],
    },
  ];

  return object;
};

export const selectCountOutboundForecast = (state) => state.count.countOutboundForecast;

export const selectCountInboundForecast = (state) => state.count.countInboundForecast;

export const selectWeeklyAnalyticsIsLoading = (state) => {
  return (
    state.count.statusObj?.["getAnalyticsCountOutboundStatus"] == "loading" ||
    state.count.statusObj?.["getAnalyticsCountInboundStatus"] == "loading"
  );
};

export const selectDailyAnalyticsIsLoading = (state) => {
  return (
    state.count.statusObj?.["getAnalyticsCountOutboundByHourStatus"] ==
    "loading" ||
    state.count.statusObj?.["getAnalyticsCountInboundByHourStatus"] == "loading"
  );
};

export const selectAnyIsLoading = (state) => {
  return (selectDailyAnalyticsIsLoading(state) || selectWeeklyAnalyticsIsLoading(state))
}


export const selectDepartureForecastIsLoading = (state) => {
  return (
    selectDailyAnalyticsIsLoading(state) ||
    selectWeeklyAnalyticsIsLoading(state)
  );
};

// booking breakdown
export const selectBookingBreakdownInbound = (state) => {
  return state.count.bookingBreakdownInbound;
};

export const selectNonPrenotifiedCount = (state) =>
  state.count.nonprenotifiedCount;
export const selectPrenotifiedCount = (state) => state.count.prenotifiedCount;

export const selectBookingBreakdownOutbound = (state) =>
  state.count.bookingBreakdownOutbound;
export const selectBookingBreakdownStatus = (state) =>
  state.count.bookingBreakdownStatus;
export const selectBookingBreakdownError = (state) =>
  state.count.bookingBreakdownError;
export const selectAnalyticsCountOutboundByHourNoReduce = (state) =>
  state.count.analyticsCountOutboundByHourNoReduce;
export const selectAnalyticsRange = (state) => state.count.range;
export const selectAnalyticsStats = (state) => state.count.analyticsStats;
export const selectAnalyticsStatsDayComparedToLastWeek = (state) =>
  state.count.analyticsStatsDayComparedToLastWeek;
export const selectAnalyticsTypeSelector = (state) => state.count.typeSelector;
export const countErrorStatus = (state) => state.count.error;
export const selectStatusObj = (state) => state.count.statusObj;
export const selectBookingsDueTimes = (state) => {
  return getDueTimesAverageHour(state?.count?.bookingsDueTimes)
};


export const noShowCountOutbound = (state) => state.count.noShowCountOutbound;

export const selectTotalNoShowCountOutbound = (state) => {
  if (state.count.countOutbound == null) {
    return 0;
  }

  let keys = Object.keys(state.count.countOutbound);
  let total = 0;
  for (let index = 0; index < keys.length; index++) {
    total = total + state.count.countOutbound[keys[index]];
  }
  return total;
};

export const selectDueTimesBetweenAirportsAverage = (state) => {
  let dueTime = state.count.bookingsDueTimesBetweenAirports;
  if (
    dueTime &&
    Object?.keys(dueTime?.dueTimeSum)?.length > 0 &&
    Object?.keys(dueTime?.dueTimeCount)?.length > 0
  ) {
    const averageHours =
      Object?.values(dueTime?.dueTimeSum)?.[0] /
      Object?.values(dueTime?.dueTimeCount)?.[0] /
      (1000 * 60 * 60);

    // Splitting the average into hours and minutes
    const hours = Math.floor(averageHours);
    const minutes = Math.round((averageHours - hours) * 60);

    // Constructing the time string
    let timeString = "";
    if (hours > 0) {
      timeString += hours === 1 ? "1 Hour" : `${hours} Hours`;
    }
    if (minutes > 0) {
      if (timeString !== "") timeString += " ";
      timeString += minutes === 1 ? "1 Minute" : `${minutes} Minutes`;
    }
    if (timeString === "") {
      timeString = "no data available";
    }

    return timeString;
  } else {
    return "no data available";
  }
};

export const selectDueTimesBetweenAirportsLoading = (state) =>
  state.count.bookingsDueTimesBetweenAirportsStatus;



export const selectMonthlyForecast = (state) => {
  state.count.countOutboundForecast;
  state.count.countInboundForecast;

  if (state.count.countOutboundForecast && Object.values(state.count.countOutboundForecast).length > 0 && state.count.countInboundForecast && Object.values(state.count.countInboundForecast).length > 0) {

    let departures = Math.round(Object.values(state.count.countOutboundForecast).reduce(
      (acc, current) => acc + current,
      0
    )
    )
    let arrivals = Math.round(
      Object.values(state.count.countInboundForecast).reduce(
        (acc, current) => acc + current,
        0
      )
    )

    return {
      departures,
      arrivals,
    };
  } else return null;
};

export const selectShowDepartures = (state) => state.count.showDepartures;
export const selectShowArrivals = (state) => state.count.showArrivals;
export const selectShowForcast = (state) => state.count.showForecast;
export const selectShowTotals = (state) => state.count.showTotals;
export const selectShowWeather = (state) => state.count.showWeather;
export const selectShowHighLights = (state) => state.count.showHighlights;


// status flags
export const selectGetBookingForecastOutboundStatus = (state) => state.count.getBookingsForecastCountOutboundStatus;
export const selectGetBookingForecastInboundStatus = (state) => state.count.getBookingsForecastCountInboundStatus;
export const selectGetBookingOutboundStatus = (state) => state.count.getBookingsCountOutboundStatus;
export const selectGetBookingInboundStatus = (state) => state.count.getBookingsCountInboundStatus;

export const selectPrenotifiedCountStatus = (state) => state.count.getPrenotifiedBookingsStatus


// Function to generate date range
function generateDateRange(startDate, endDate) {

  const start = new Date(startDate);
  const end = new Date(endDate);
  const dateArray = [];
  while (start <= end) {
    const dateStr = start.toISOString().split('T')[0];
    dateArray.push(dateStr);
    start.setDate(start.getDate() + 1);
  }

  return dateArray;
}

// Function to round to the next quarter hour
function roundToNextQuarterHour2(isoString) {
  const date = new Date(isoString);
  const minutes = date.getMinutes();
  const quarter = Math.ceil(minutes / 15) * 15;
  date.setMinutes(quarter);

  return date.toISOString().split("T")[1].slice(0, 5);
}

// Function to zero pad numbers
function zeroPad2(num, places) {
  return String(num).padStart(places, '0');
}

export const selectDeparturesArrivalsRangeCountByQuarterHourForecast = (state) => {
  if (!state.count.arrivalsForecastRange || !state.count.departuresForecastRange) {
    return {};
  }

  // get start and end dates
  const startDate = state.count.arrivalsForecastRange.startDate;
  const endDate = state.count.arrivalsForecastRange.endDate;

  // use forecast data
  const inboundData = state.count.arrivalsForecastRange.bookings ?? [];
  const outboundData = state.count.departuresForecastRange.bookings ?? [];

  // mapping function that process each flight and stores the average passenger count in the correct time interval
  const processFlightData = (flightData) => {
    let mappingObject = {};

    flightData.forEach((flight) => {
      let isoString = flight.key[6];
      let flightDate = flight.key[1]; // This is the date of the flight
      let average = flight.value;

      // find which interval the flight belongs to
      let interval = roundToNextQuarterHour2(isoString);

      // Initialize the date key if it doesn't exist
      if (!mappingObject[flightDate]) {
        mappingObject[flightDate] = {};
      }

      // add the average to the interval in the mapping object under the correct date
      if (mappingObject[flightDate][interval]) {
        mappingObject[flightDate][interval] += average;
      } else {
        mappingObject[flightDate][interval] = average;
      }
    });

    return mappingObject;
  };

  // create objects to store mapping
  let inboundMapping = processFlightData(inboundData);
  let outboundMapping = processFlightData(outboundData);

  // Function to generate the final graph data for each date
  const generateGraphDataForDate = (date, inboundMapping, outboundMapping, multiplier) => {
    let graphData = [];

    for (let i = 0; i < 24 * 4; i++) {
      let hour = Math.floor(i / 4);
      let minute = (i % 4) * 15;
      let intervalKey = zeroPad2(hour, 2) + ":" + zeroPad2(minute, 2);

      graphData.push({
        name: intervalKey,
        Departures: multiplier ? Math.round(outboundMapping[date]?.[intervalKey] + (outboundMapping[date]?.[intervalKey] * multiplier)) : Math.round(outboundMapping[date]?.[intervalKey]) || 0,
        Arrivals: multiplier ? Math.round(inboundMapping[date]?.[intervalKey] + (inboundMapping[date]?.[intervalKey] * multiplier)) : Math.round(inboundMapping[date]?.[intervalKey]) || 0,

      });
    }

    return graphData;
  };

  // Function to map bookings to dates
  function mapBookingsToDates(startDate, endDate) {
    const dateRange = generateDateRange(startDate, endDate);
    const seasonalVariances = state.seasonalVariance.seasonVarianceOfIATA
    const seasonalVarianceFilter = state.date.seasonalVarianceFilter
    const dateMap = {};
    dateRange.forEach(date => {
      if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {
        seasonalVariances.seasonalAdjustments.forEach((variance) => {
          let varianceStartDate = new Date(variance.startDate);
          let varianceEndDate = new Date(variance.endDate);
          let bookingsDate = new Date(date);

          if (bookingsDate >= varianceStartDate && bookingsDate <= varianceEndDate) {
            dateRange.forEach(date => {
              dateMap[date] = generateGraphDataForDate(date, inboundMapping, outboundMapping, variance.multiplier);
            });
          } else {
            dateRange.forEach(date => {
              dateMap[date] = generateGraphDataForDate(date, inboundMapping, outboundMapping);
            });
          }
        })

      } else {
        dateRange.forEach(date => {
          dateMap[date] = generateGraphDataForDate(date, inboundMapping, outboundMapping);
        });
      }
    });

    return dateMap;
  }

  // Map the graph data to the correct dates
  const result = mapBookingsToDates(startDate, endDate);

  return result;
};

export const selectDeparturesArrivalsRangeCountByHourForecast = (state) => {
  if (!state?.count?.arrivalsForecastRange || !state?.count?.departuresForecastRange) {
    return {};
  }

  // get start and end dates
  const startDate = state?.count?.arrivalsForecastRange?.startDate;
  const endDate = state?.count?.arrivalsForecastRange?.endDate;

  // use forecast data
  let inboundData = state?.count?.arrivalsForecastRange?.bookings ?? [];
  let outboundData = state?.count?.departuresForecastRange?.bookings ?? [];

  // create objects to store mapping for each date
  let inboundMapping = {};
  let outboundMapping = {};

  // for each booking in inbound data
  inboundData?.forEach(i => {
    let hour = i.key[2];
    let flightDate = i.key[1]; // Extract the flight date

    // Initialize the date key if it doesn't exist
    if (!inboundMapping[flightDate]) {
      inboundMapping[flightDate] = {};
    }

    if (inboundMapping[flightDate][hour]) {
      inboundMapping[flightDate][hour] += i?.value;
    } else {
      inboundMapping[flightDate][hour] = i?.value;
    }
  });

  // for each booking in outbound data
  outboundData?.forEach(i => {
    let hour = i.key[2];
    let flightDate = i.key[1]; // Extract the flight date

    // Initialize the date key if it doesn't exist
    if (!outboundMapping[flightDate]) {
      outboundMapping[flightDate] = {};
    }

    if (outboundMapping[flightDate][hour]) {
      outboundMapping[flightDate][hour] += i?.value;
    } else {
      outboundMapping[flightDate][hour] = i?.value;
    }
  });

  // Function to generate graph data for each hour for a specific date
  const generateGraphDataForDate = (date, multiplier) => {
    let graphData = [];

    for (let hour = 0; hour < 24; hour++) {
      let hourStr = zeroPad(hour, 2);
      graphData.push({
        name: `${hourStr}:00`,
        Departures: multiplier ? Math.round(outboundMapping[date]?.[hourStr] + (outboundMapping[date]?.[hourStr] * multiplier)) : Math.round(outboundMapping[date]?.[hourStr]) || 0,
        Arrivals: multiplier ? Math.round(inboundMapping[date]?.[hourStr] + (inboundMapping[date]?.[hourStr] * multiplier)) : Math.round(inboundMapping[date]?.[hourStr]) || 0,

        // Arrivals: Math.round(inboundMapping[date]?.[hourStr]) || 0,
      });
    }

    return graphData;
  };

  // Function to map bookings to dates
  function mapBookingsToDates(startDate, endDate) {
    const dateRange = generateDateRange(startDate, endDate);

    const seasonalVariances = state.seasonalVariance.seasonVarianceOfIATA
    const seasonalVarianceFilter = state.date.seasonalVarianceFilter
    const dateMap = {};

    dateRange.forEach(date => {
      if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {
        seasonalVariances.seasonalAdjustments.forEach((variance) => {
          let varianceStartDate = new Date(variance.startDate);
          let varianceEndDate = new Date(variance.endDate);
          let bookingsDate = new Date(date);

          if (bookingsDate >= varianceStartDate && bookingsDate <= varianceEndDate) {
            dateRange.forEach(date => {
              dateMap[date] = generateGraphDataForDate(date, variance.multiplier);
            });
          } else {
            dateRange.forEach(date => {
              dateMap[date] = generateGraphDataForDate(date);
            });
          }
        })

      } else {
        dateRange.forEach(date => {
          dateMap[date] = generateGraphDataForDate(date);
        });
      }
    });

    return dateMap;
  }

  // Map the graph data to the correct dates
  const result = mapBookingsToDates(startDate, endDate);

  return result;
};

export const selectDeparturesForecastRangeStatus = (state) => state.count.departuresForecastRangeStatus;
export const selectArrivalsForecastRangeStatus = (state) => state.count.arrivalsForecastRangeStatus;
export const selectDeparturesForecastStatus = (state) => state.count.departuresForecastStatus;
export const selectArrivalsForecastStatus = (state) => state.count.arrivalsForecastStatus;
export const selectAnalyticsStatsStatus = (state) => state.count.analyticsStatsStatus;


export const selectDeparturesFlightsForecasStatus = (state) => state.count.departuresFlightsForecastRangeStatus
export const selectArrivalsFlightsForecastStatus = (state) => state.count.arrivalsFlightsForecastRangeStatus

export default bookingsCountSlice.reducer;
